import {
  type ChangeEvent,
  type MouseEvent,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'
import { type ColorChangeHandler, SketchPicker } from 'react-color'

import { StringsContext } from '@lib/strings-context'

import Accordion from '@components/accordion'
import Icon from '@components/icon'

interface CombinedListingFramePreviewSettingsProps {
  defaultBackgroundColor: string
  predefinedBackgroundColors: string[]
  onFileChange: (fileUrl: string | null) => void
  onBackgroundColorChange: (color: string) => void
}

interface FileInput {
  url?: string
  name?: string
}

const CombinedListingFramePreviewSettings = ({
  defaultBackgroundColor,
  predefinedBackgroundColors,
  onFileChange,
  onBackgroundColorChange,
}: CombinedListingFramePreviewSettingsProps) => {
  const strings = useContext(StringsContext)

  const [fileInput, setFileInput] = useState<FileInput>({})
  const [showFileError, setShowFileError] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [backgroundColor, setBackgroundColor] = useState(defaultBackgroundColor)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setShowFileError(false)

      const files = event.target.files

      if (!files || files.length === 0) {
        return
      }

      let file: File | undefined

      for (let index = 0; index < files.length; index += 1) {
        if (files[index].type.match(/^image\//)) {
          file = files[index]
          break
        }
      }

      if (!file) {
        setShowFileError(true)
        onFileChange(null)
        setFileInput({})
        return
      }

      const fileUrl = URL.createObjectURL(file)
      setFileInput({
        url: fileUrl,
        name: file.name,
      })
      onFileChange(fileUrl)
    },
    [onFileChange, setFileInput, setShowFileError],
  )

  const handleFileRemove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      onFileChange(null)
      setFileInput({})
    },
    [onFileChange, setFileInput],
  )

  const handleColorChange: ColorChangeHandler = useCallback(
    (color) => {
      setBackgroundColor(color.hex)
      onBackgroundColorChange(color.hex)
    },
    [onBackgroundColorChange, setBackgroundColor],
  )

  return (
    <Accordion
      id="combined-listing-frame-preview-settings"
      title={strings.combinedListingPreviewSettingsTitle}
      className="max-w-sm"
    >
      <div className="space-y-3">
        <span className="text-sm">
          {strings.combinedListingPreviewSettingsUploadDescription}
        </span>

        <label className="flex items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <span className="inline-flex whitespace-nowrap px-4 py-1.5 border border-black rounded-full text-sm cursor-pointer">
            {strings.combinedListingPreviewSettingsUploadLabel}
          </span>

          {!!fileInput.name && (
            <>
              <span className="ml-3 text-sm cursor-pointer">
                {fileInput.name}
              </span>
              <button onClick={handleFileRemove} className="ml-1">
                <Icon
                  id="combined-listing-background-image-remove"
                  name="Cross"
                />
              </button>
            </>
          )}
        </label>

        {showFileError && (
          <div className="py-1 text-sm text-error">
            {strings.combinedListingPreviewSettingsUploadError}
          </div>
        )}
      </div>

      <div className="relative mt-8">
        <div
          onClick={() =>
            setShowColorPicker((showColorPicker) => !showColorPicker)
          }
          className="relative inline-flex items-center gap-4 cursor-pointer"
        >
          <div className="inline-flex justify-center items-center p-1 border border-input-border">
            <div
              className="w-[22px] h-[22px]"
              style={{
                backgroundColor,
              }}
            />
          </div>
          <span className="text-sm">
            {strings.combinedListingPreviewSettingsColorLabel}
          </span>
        </div>

        {showColorPicker && (
          <div className="absolute py-4 z-10">
            <div
              onClick={() => setShowColorPicker(false)}
              className="fixed inset-0"
            />
            <SketchPicker
              color={backgroundColor}
              presetColors={predefinedBackgroundColors}
              width="320px"
              disableAlpha
              onChange={handleColorChange}
            />
          </div>
        )}
      </div>
    </Accordion>
  )
}

export default CombinedListingFramePreviewSettings
